/* ============================================================
   MOSAIK — SÉLECTION DE ZONE (desktop)

   Permet de délimiter une PARTIE du plan et d'y appliquer « Remplir tout »
   et « Damier » sans toucher au reste : on dessine d'abord une zone, puis on
   clique le bouton, qui ne s'applique qu'à l'intérieur.

   C'est un MODE, ouvert et refermé par un bouton — « Sélectionner une zone »
   dans la carte « Ajuster votre espace ». Tant qu'il est armé le plan ne se
   peint plus : chaque clic sert à délimiter.

     1. clic sur « Sélectionner une zone » -> le mode s'arme (bouton bleu,
        libellé « Terminer »)
     2. clic sur le plan   -> ancre un coin, le cadre bleu s'ouvre
     3. la souris se déplace -> le cadre suit le curseur, bouton relâché
     4. clic               -> valide ; la zone reste affichée et la carte
        « Dalles » s'éclaire : c'est là que se joue le remplissage
     5. « Terminer » (ou Échap) -> ferme le mode et relâche la zone

   Le geste passait autrefois par un double-clic sur le plan, sans bouton : il
   fallait alors défaire la dalle peinte par le premier clic, et rien ne disait
   au visiteur que la fonction existait.

   Les clics du mode sont avalés en phase de capture, donc le contrôleur ne
   peint jamais pendant qu'on sélectionne.

   AU DOIGT, le geste est d'un seul tenant — appuyer sur un coin, glisser,
   relâcher — plutôt que les deux clics du bureau : c'est le geste que le
   tactile attend, et le seul possible ici (sans souris, rien ne suit un cadre
   « bouton relâché »). Il aboutit à la même zone, par le même code : c'est
   mosaik-zoom.js, qui porte les événements tactiles du plan, qui appelle
   touchBegin / touchMove / touchEnd ci-dessous dès que le mode est armé —
   sinon le doigt peindrait.

   AFFICHAGE. Le cadre est dessiné sur un <canvas> de recouvrement posé sur le
   plan, jamais dans le plan lui-même : redessiner le plan (draw(), asynchrone,
   qui attend les images des dalles) à chaque mousemove serait impossible à
   suivre. Ce calque recopie la position ET la transformation CSS du plan, donc
   il suit le zoom et le déplacement sans rien savoir d'eux.

   LA ZONE EST STOCKÉE EN DALLES (c0..c1, r0..r1), pas en pixels : un
   redimensionnement, un zoom ou un réaffichage la retrouvent telle quelle.
   ============================================================ */
window.MosaikSelect = {

  m:null, cv:null, frame:null, ov:null, octx:null,

  /* {x,y} en pixels internes du canvas : le coin ancré par le double-clic.
     Non nul = un cadre est en cours de tracé. */
  anchor:null,
  /* cadre libre en cours (pixels internes), tel que la souris le dessine */
  live:null,
  /* zone validée, EN DALLES et bornes incluses : {c0,r0,c1,r1} */
  zone:null,

  /* le mode est-il armé ? (bouton « Sélectionner une zone » enfoncé) */
  armed:false,

  MIN_DRAG:6,          // px : en deçà, le clic de validation est un clic « pour rien »

  /* Horodatage du dernier geste tactile. Un appui sur le plan émet aussi, sur la
     plupart des navigateurs, un `click` de compatibilité : sans ce garde-fou il
     rouvrirait un cadre à la souris juste après celui qu'on vient de tracer au
     doigt, et une ancre resterait pendante. */
  _lastTouch:0,
  TOUCH_GUARD:600,     // ms
  fromTouch:function(){ return (Date.now() - this._lastTouch) < this.TOUCH_GUARD; },

  /* Pendant tout le mode, le contrôleur ne doit ni peindre ni gommer : les clics
     sur le plan servent à délimiter, pas à poser une dalle. */
  blocksPaint:function(){ return !!this.anchor || this.armed; },

  setup:function(mosaik){
    const self = this, cv = mosaik.grid;
    if(!cv) return;
    this.m = mosaik;
    this.cv = cv;
    this.frame = cv.parentElement;             // #mosaik-canvas (position:relative)
    this._buildOverlay();

    // le bouton qui ouvre et referme le mode
    const btn = document.getElementById('mosaik-select-zone-btn');
    if(btn) btn.addEventListener('click', () => self.toggle());

    // Le cadre suit la souris, bouton relâché — y compris hors du plan (on borne).
    window.addEventListener('mousemove', e => {
      if(!self.anchor || self.fromTouch()) return;
      const p = self._toInternal(e.clientX, e.clientY);
      self.live = { x0:self.anchor.x, y0:self.anchor.y, x1:p.x, y1:p.y };
      self.render();
    });

    /* Capture sur window : ces clics-ci ne doivent atteindre NI le contrôleur
       (qui peindrait la dalle sous le curseur) ni le reste de la page.
         - clic qui ancre le premier coin, mode armé
         - clic qui valide le cadre
       Le bouton du mode, lui, n'est pas sur le plan : son propre gestionnaire
       s'exécute normalement (la capture ci-dessous le laisse passer). */
    window.addEventListener('click', e => {
      if(self.fromTouch()) return;            // le doigt a son propre chemin
      const onPlan = !!(self.frame && self.frame.contains(e.target));
      if(self.anchor){
        self.commit();
        if(onPlan){ e.preventDefault(); e.stopPropagation(); }
        return;
      }
      if(self.armed && onPlan){
        if(window.MosaikZoom && MosaikZoom.blocksPaint()) return;   // outil main / pan en cours
        e.preventDefault(); e.stopPropagation();
        self.begin(e.clientX, e.clientY);
      }
    }, true);

    document.addEventListener('keydown', e => {
      if(e.key !== 'Escape' && e.key !== 'Esc') return;
      if(!self.armed && !self.anchor && !self.zone) return;
      self.disarm();
    });

    // Le plan change de géométrie : la zone ne veut plus rien dire.
    ['mosaik-collection','mosaik-area-width','mosaik-area-height'].forEach(id => {
      const el = document.getElementById(id);
      if(el) el.addEventListener('change', () => self.clear());
    });

    // zoom / déplacement : le plan porte une transformation CSS, le calque la recopie
    if(window.MutationObserver){
      new MutationObserver(() => self.render())
        .observe(cv, { attributes:true, attributeFilter:['style'] });
    }

    // « Effacer la zone » relâche la zone SANS quitter le mode : on en retrace
    // une autre dans la foulée. Seul « Terminer » (ou Échap) ferme le mode.
    const clr = document.getElementById('mosaik-selection-clear');
    if(clr) clr.addEventListener('click', () => self.clear());

    /* Un remplissage lancé, la carte « Dalles » n'a plus à être signalée : le
       visiteur y est. La zone et le mode, eux, restent — on enchaîne souvent
       plusieurs remplissages sur la même zone. */
    ['mosaik-solid-mode','mosaik-checkerboard-mode'].forEach(id => {
      const el = document.getElementById(id);
      if(el) el.addEventListener('click', () => self.spotlight(false));
    });

    this.syncUI();
  },

  /* ---------- le mode ---------- */

  toggle:function(){ this.armed ? this.disarm() : this.arm(); },

  arm:function(){
    this.armed = true;
    this.anchor = null; this.live = null; this.zone = null;
    // la croix dès le survol : le plan n'attend plus un coup de pinceau
    if(this.frame) this.frame.classList.add('mosaik-select-armed');
    this.syncUI();
    this.render();
    /* Empilé (mobile, tablette), le bouton est SOUS le plan : une fois armé, le
       plan est hors de l'écran et le mode semble n'avoir rien fait. On y amène
       le visiteur — c'est là que le geste se joue. */
    const stage = document.getElementById('mosaik-stage');
    const split = window.matchMedia('(min-width:1025px)').matches;
    if(!split && stage && stage.scrollIntoView)
      stage.scrollIntoView({ block:'nearest', behavior:'smooth' });
  },

  /* Sortie du mode (« Terminer », Échap) : la zone tombe, donc « Remplir tout »
     et « Damier » retrouvent le plan entier. */
  disarm:function(){
    this.armed = false;
    if(this.frame) this.frame.classList.remove('mosaik-select-armed');
    this.clear();
  },

  /* Premier clic sur le plan, mode armé : on ancre un coin. */
  begin:function(clientX, clientY){
    this.zone = null;
    this.anchor = this._toInternal(clientX, clientY);
    this.live = { x0:this.anchor.x, y0:this.anchor.y, x1:this.anchor.x, y1:this.anchor.y };
    if(this.frame) this.frame.classList.add('mosaik-selecting');
    this.syncUI();
    this.render();
  },

  /* ---------- le même geste, au doigt ----------
     Appelés par mosaik-zoom.js tant que le mode est armé (le doigt ne peint pas
     alors). Un seul geste : appui = coin ancré, glissé = le cadre suit, relâché
     = la zone est prise. Tout passe par les mêmes begin/commit que la souris,
     donc le résultat est le même objet `zone`, à la dalle près. */
  touchBegin:function(clientX, clientY){
    this._lastTouch = Date.now();
    this.begin(clientX, clientY);
  },
  touchMove:function(clientX, clientY){
    this._lastTouch = Date.now();
    if(!this.anchor) return;
    const p = this._toInternal(clientX, clientY);
    this.live = { x0:this.anchor.x, y0:this.anchor.y, x1:p.x, y1:p.y };
    this.render();
  },
  touchEnd:function(){
    this._lastTouch = Date.now();
    if(this.anchor) this.commit();
  },

  /* Un deuxième doigt s'est posé : le geste n'était pas un tracé, c'est un
     pincement. Le cadre en cours tombe, le mode reste armé. */
  abortDraft:function(){
    if(!this.anchor && !this.live) return;
    this._lastTouch = Date.now();
    this.anchor = null; this.live = null;
    if(this.frame) this.frame.classList.remove('mosaik-selecting');
    this.syncUI();
    this.render();
  },

  /* Halo sur la carte « Dalles » : une fois la zone tracée, c'est là qu'il faut
     aller (choisir les couleurs, puis « Remplir la zone » / « Damier »). On
     déplie la carte au passage — signaler une carte fermée n'aiderait personne. */
  spotlight:function(on){
    const card = document.getElementById('mosaik-card-tiles');
    if(!card) return;
    if(!on){ card.classList.remove('mosaik-attn'); return; }
    if(window.MosaikUI && MosaikUI.openCard) MosaikUI.openCard('mosaik-card-tiles');
    else card.classList.remove('collapsed');
    // retiré puis reposé : l'animation doit repartir à chaque nouvelle zone
    card.classList.remove('mosaik-attn');
    void card.offsetWidth;
    card.classList.add('mosaik-attn');
    if(card.scrollIntoView) card.scrollIntoView({ block:'nearest', behavior:'smooth' });
  },

  /* ---------- zone ---------- */

  /* Valide le cadre en cours : il devient une plage de dalles. Un cadre
     minuscule (double-clic puis clic sans bouger) n'est pas une sélection. */
  commit:function(){
    const live = this.live, L = this.m && this.m.layout;
    this.anchor = null; this.live = null;
    if(this.frame) this.frame.classList.remove('mosaik-selecting');
    if(live && L && (Math.abs(live.x1 - live.x0) >= this.MIN_DRAG || Math.abs(live.y1 - live.y0) >= this.MIN_DRAG)){
      const col = x => Math.max(0, Math.min(L.cols - 1, Math.floor((x - L.xOff) / L.tileW))),
            row = y => Math.max(0, Math.min(L.rows - 1, Math.floor((y - L.yOff) / L.tileH))),
            c = [col(live.x0), col(live.x1)].sort((a, b) => a - b),
            r = [row(live.y0), row(live.y1)].sort((a, b) => a - b);
      this.zone = { c0:c[0], c1:c[1], r0:r[0], r1:r[1] };
    }
    this.spotlight(!!this.zone);
    this.syncUI();
    this.render();
  },

  clear:function(){
    this.anchor = null; this.live = null; this.zone = null;
    if(this.frame) this.frame.classList.remove('mosaik-selecting');
    this.spotlight(false);
    this.syncUI();
    this.render();
  },

  /* La dalle `idx` est-elle dans la zone ? Sans zone, TOUT le plan répond oui —
     c'est ce qui fait que « Remplir tout » garde son sens habituel. */
  has:function(idx, cols){
    const z = this.zone;
    if(!z) return true;
    const c = idx % cols, r = Math.floor(idx / cols);
    return c >= z.c0 && c <= z.c1 && r >= z.r0 && r <= z.r1;
  },

  /* ---------- calque d'affichage ---------- */

  _buildOverlay:function(){
    if(!this.frame) return;
    const ov = document.createElement('canvas');
    ov.id = 'mosaik-select-ov';
    // hors du PDF : html2canvas saute les éléments ainsi marqués (mosaik-pdf.js)
    ov.setAttribute('data-html2canvas-ignore', 'true');
    ov.style.cssText = 'position:absolute; left:0; top:0; pointer-events:none; z-index:4; transform-origin:0 0;';
    this.frame.appendChild(ov);
    this.ov = ov;
    this.octx = ov.getContext('2d');
  },

  /* Rectangle à peindre, en pixels internes du plan : le cadre libre s'il est en
     cours de tracé, sinon la zone validée ramenée à ses dalles. */
  _rects:function(){
    const L = this.m && this.m.layout;
    if(!L) return null;
    const out = {};
    if(this.zone && !this.live){
      const z = this.zone;
      out.snapped = { x:L.xOff + z.c0 * L.tileW, y:L.yOff + z.r0 * L.tileH,
                      w:(z.c1 - z.c0 + 1) * L.tileW, h:(z.r1 - z.r0 + 1) * L.tileH };
      return out;
    }
    if(this.live){
      const l = this.live;
      out.free = { x:Math.min(l.x0, l.x1), y:Math.min(l.y0, l.y1),
                   w:Math.abs(l.x1 - l.x0), h:Math.abs(l.y1 - l.y0) };
      // aperçu des dalles qui seront prises : le cadre libre suit la souris au
      // pixel près, mais ce sont bien des dalles entières qui seront remplies
      const col = x => Math.max(0, Math.min(L.cols - 1, Math.floor((x - L.xOff) / L.tileW))),
            row = y => Math.max(0, Math.min(L.rows - 1, Math.floor((y - L.yOff) / L.tileH))),
            c0 = col(out.free.x), c1 = col(out.free.x + out.free.w),
            r0 = row(out.free.y), r1 = row(out.free.y + out.free.h);
      out.snapped = { x:L.xOff + c0 * L.tileW, y:L.yOff + r0 * L.tileH,
                      w:(c1 - c0 + 1) * L.tileW, h:(r1 - r0 + 1) * L.tileH };
      return out;
    }
    return null;
  },

  /* Le calque colle au plan : même boîte, même transformation CSS (zoom / pan),
     et une résolution qui suit la densité de l'écran. Appelé après chaque draw()
     (mosaik-render.js) et à chaque mouvement de souris. */
  render:function(){
    const ov = this.ov, cv = this.cv;
    if(!ov || !cv) return;
    // Boîte de BORDURE, pas de contenu : c'est elle que la transformation CSS du
    // zoom met à l'échelle (transform-origin 0 0). Le calque doit donc avoir la
    // même, sinon il dérive du plan dès qu'on zoome. Le décalage de la bordure du
    // <canvas> (1px) est ensuite repris dans le contexte, où l'on dessine en
    // coordonnées de plan.
    const w = cv.offsetWidth, h = cv.offsetHeight,
          bx = cv.clientLeft, by = cv.clientTop;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    if(ov.width !== Math.round(w * dpr) || ov.height !== Math.round(h * dpr)){
      ov.width = Math.round(w * dpr); ov.height = Math.round(h * dpr);
    }
    ov.style.left = cv.offsetLeft + 'px';
    ov.style.top  = cv.offsetTop  + 'px';
    ov.style.width = w + 'px';
    ov.style.height = h + 'px';
    ov.style.transform = cv.style.transform;

    const ctx = this.octx;
    ctx.setTransform(dpr, 0, 0, dpr, bx * dpr, by * dpr);
    ctx.clearRect(-bx, -by, w, h);
    const r = this._rects();
    if(!r) return;
    /* Le sol est le plus souvent NOIR : un simple trait bleu s'y perd. Chaque
       trait est donc doublé d'un liseré blanc en dessous — lisible sur une dalle
       sombre comme sur une dalle claire. */
    const line = (x, y, w, h, dash, width) => {
      ctx.setLineDash([]);
      ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.lineWidth = width + 2;
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash(dash || []);
      ctx.strokeStyle = '#699bb4'; ctx.lineWidth = width;
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
    };
    // les dalles concernées, en aplat bleu translucide
    if(r.snapped){
      ctx.fillStyle = 'rgba(105,155,180,.28)';
      ctx.fillRect(r.snapped.x, r.snapped.y, r.snapped.w, r.snapped.h);
      if(!r.free) line(r.snapped.x + 1, r.snapped.y + 1, r.snapped.w - 2, r.snapped.h - 2, null, 2);
    }
    // le cadre libre : pointillés, comme une sélection du bureau
    if(r.free) line(r.free.x + .5, r.free.y + .5, r.free.w, r.free.h, [5, 4], 1);
  },

  /* ---------- barre latérale ---------- */

  /* Les deux boutons de remplissage disent ce qu'ils vont faire : tout le plan,
     ou la seule zone sélectionnée. */
  syncUI:function(){
    const m = this.m; if(!m) return;
    const z = this.zone, t = s => m.getText(s);
    const label = (id, key) => {
      const el = document.getElementById(id), sp = el && el.getElementsByTagName('span')[0];
      if(sp) sp.innerText = t(key);
    };
    label('mosaik-solid-mode',        z ? 'Fill selection' : 'Fill all');
    label('mosaik-checkerboard-mode', z ? 'Checkerboard in selection' : 'Checkerboard');
    // le bouton du mode : bleuté et intitulé « Terminer » tant qu'on y est
    const btn = document.getElementById('mosaik-select-zone-btn');
    if(btn){
      btn.classList.toggle('sel', this.armed);
      btn.setAttribute('aria-pressed', this.armed ? 'true' : 'false');
    }
    const blbl = document.getElementById('mosaik-select-zone-label');
    if(blbl) blbl.innerText = t(this.armed ? 'Done' : 'Select a zone');
    const row = document.getElementById('mosaik-selection-row');
    if(row) row.hidden = !z;
    const info = document.getElementById('mosaik-selection-info');
    if(info && z) info.innerText = t('Selected zone') + ' : ' + (z.c1 - z.c0 + 1) + ' × ' + (z.r1 - z.r0 + 1) + ' ' + t('tiles');
    const clr = document.getElementById('mosaik-selection-clear');
    if(clr) clr.innerText = t('Clear selection');
    /* La consigne suit l'étape en cours : à quoi sert le mode, comment tracer,
       puis quoi faire de la zone une fois tracée. Celle du tracé est dite dans
       le geste de l'écran — deux clics à la souris, un glissé au doigt : c'est
       exactement le même critère qui route les événements (mosaik-zoom.js), la
       consigne ne peut donc pas décrire un geste qui n'a pas cours. */
    const hint = document.getElementById('mosaik-selection-hint');
    const touch = window.matchMedia('(max-width:768px)').matches;
    if(hint) hint.innerText = t(!this.armed
      ? 'Select a zone to fill only a part of the plan.'
      : (z ? 'Pick your colours, then click Fill selection or Checkerboard. Done to leave.'
           : (touch ? 'Press a corner of the zone and slide your finger to the opposite one.'
                    : 'Click a corner of the zone, move the mouse, then click to confirm (Esc to cancel).')));
  },

  /* point écran -> pixels internes du plan (le zoom est une transformation CSS,
     donc on passe par la boîte réellement affichée). Borné au plan : le cadre
     peut être tiré au-delà du bord sans sortir du dessin. */
  _toInternal:function(clientX, clientY){
    const cv = this.cv, r = cv.getBoundingClientRect();
    const x = (clientX - r.left) / r.width  * cv.clientWidth,
          y = (clientY - r.top)  / r.height * cv.clientHeight;
    return { x:Math.max(0, Math.min(cv.clientWidth,  x)),
             y:Math.max(0, Math.min(cv.clientHeight, y)) };
  }
};
